import {
  AlignItems,
  Box,
  Color,
  Display,
  FontWeight,
  Icon,
  IconVariant,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'

type DashboardActionButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'onClick'
> & {
  actionType: string
  children: React.ReactNode
  onClick?: (actionType: string, event: React.MouseEvent<HTMLButtonElement>) => void
}

const StyledButton = withCitrusTheme('button', (theme) => ({
  appearance: 'none',
  alignItems: 'center',
  backgroundColor: theme.color(Color.White),
  border: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  borderRadius: '6px',
  boxShadow: 'none',
  color: theme.color(Color.ColdGrey_800),
  cursor: 'pointer',
  display: 'flex',
  gap: `${theme.spacing(3)}px`,
  justifyContent: 'space-between',
  minHeight: '48px',
  padding: `${theme.spacing(3)}px ${theme.spacing(4)}px`,
  textAlign: 'left',
  width: '100%',
  '&:hover': {
    borderColor: theme.color(Color.ColdGrey_200),
  },
  '&:focus': {
    outline: `2px solid ${theme.color(Color.Secondary_400)}`,
    outlineOffset: '2px',
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
}))

export function DashboardActionButton({
  actionType,
  children,
  onClick,
  type = 'button',
  ...props
}: DashboardActionButtonProps) {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(actionType, event)
  }

  return (
    <StyledButton {...props} data-action-type={actionType} onClick={handleClick} type={type}>
      <Text color={Color.ColdGrey_800} fontWeight={FontWeight.Bold} size={3}>
        {children}
      </Text>
      <Box
        color={Color.Secondary_400}
        display={Display.Flex}
        alignItems={AlignItems.Center}
        style={{ flexShrink: 0 }}
      >
        <Icon aria-hidden="true" name="arrow_right" size={6} variant={IconVariant.Inherit} />
      </Box>
    </StyledButton>
  )
}
